import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Send, Check, X } from 'lucide-react';
import { matterChat, applyMatterChat, getMatter } from '../api/matters';
import Button from '../components/ui/Button';
import type { MatterDetail, MatterMessage, ClauseUpdate, Clause } from '../types';

const numToken = (s: string) => (s || '').match(/\d+(?:\.\d+)*/)?.[0] || '';

function clip(text: string, n = 240): string {
  const t = (text || '').trim();
  return t.length > n ? t.slice(0, n) + '…' : t;
}

// One reviewable proposed change with old→new text and flag deltas.
function UpdateCard({ u, clause, checked, onToggle }:
  { u: ClauseUpdate; clause: Clause | undefined; checked: boolean; onToggle: () => void }) {
  const oldText = clause ? (clause.current_text ?? clause.original_text ?? '') : '';
  const flags: string[] = [];
  if (u.is_risk === true) flags.push('поставить риск');
  if (u.is_risk === false) flags.push('снять риск');
  if (u.include_in_protocol === true) flags.push('в протокол');
  if (u.include_in_protocol === false) flags.push('из протокола');

  return (
    <div className={`rounded-lg border p-3 ${checked ? 'border-pink-200 bg-pink-50/40' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
      <div className="flex items-start gap-2">
        <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 accent-pink-500" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-gray-800">{u.clause ? `п. ${u.clause}` : 'пункт'}</span>
            {u.is_new && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">новый пункт</span>}
            {flags.map((f) => (
              <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">{f}</span>
            ))}
          </div>
          {u.comment && <p className="text-xs text-gray-500 mb-2">{u.comment}</p>}
          {u.new_text && (
            <div className="space-y-1.5">
              {!u.is_new && oldText && (
                <div className="text-[13px] leading-relaxed">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">Было</span>
                  <p className="whitespace-pre-wrap text-gray-500">{clip(oldText)}</p>
                </div>
              )}
              <div className="text-[14px] leading-relaxed">
                <span className="text-[10px] uppercase tracking-wide text-emerald-600">Станет</span>
                {/* Full proposed redaction (no truncation) — this is what gets applied. */}
                <div className="max-h-72 overflow-auto rounded bg-emerald-50/40 p-2">
                  <p className="whitespace-pre-wrap text-gray-800">{u.new_text}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Diff block + apply/reject controls for one assistant message with proposals.
function ProposalBlock({ matter, message, onMatterChanged }:
  { matter: MatterDetail; message: MatterMessage; onMatterChanged: (m: MatterDetail) => void }) {
  const updates = message.updates || [];
  const [accept, setAccept] = useState<boolean[]>(() => updates.map(() => true));
  const [busy, setBusy] = useState<'' | 'apply' | 'reject'>('');
  const [err, setErr] = useState<string | null>(null);
  const byNum = new Map<string, Clause>();
  for (const c of matter.clauses) { const n = numToken(c.number); if (n) byNum.set(n, c); }

  const apply = async (idx: number[]) => {
    setBusy(idx.length ? 'apply' : 'reject'); setErr(null);
    try { onMatterChanged(await applyMatterChat(matter.id, message.id, idx)); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Не удалось применить'); }
    finally { setBusy(''); }
  };

  const acceptedIdx = accept.map((v, i) => (v ? i : -1)).filter((i) => i >= 0);

  return (
    <div className="mt-2 space-y-2">
      {updates.map((u, i) => (
        <UpdateCard key={i} u={u} clause={byNum.get(numToken(u.clause))}
          checked={accept[i]} onToggle={() => setAccept((a) => a.map((v, j) => j === i ? !v : v))} />
      ))}
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => apply(acceptedIdx)} disabled={!!busy || acceptedIdx.length === 0} loading={busy === 'apply'}>
          <Check className="w-4 h-4" /> Применить выбранное ({acceptedIdx.length})
        </Button>
        <Button size="sm" variant="secondary" onClick={() => apply([])} disabled={!!busy} loading={busy === 'reject'}>
          <X className="w-4 h-4" /> Отклонить все
        </Button>
      </div>
    </div>
  );
}

export default function MatterChat({ matter, onMatterChanged }:
  { matter: MatterDetail; onMatterChanged: (m: MatterDetail) => void }) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const messages = matter.messages || [];

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true); setErr(null);
    try {
      await matterChat(matter.id, msg);
      // Reload so the new user+assistant messages (with proposed updates) appear.
      const updated = await getMatter(matter.id);
      onMatterChanged(updated);
      setInput('');  // clear only after the turn fully succeeded
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Чат не удался');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Чат по договору</h3>

      <div className="space-y-3 max-h-[32rem] overflow-auto mb-3">
        {messages.length === 0 && (
          <p className="text-sm text-gray-400">
            Спросите по договору или попросите внести правки: напр. «по п. 5.2 сними риск и сократи срок до 10 рд».
            Я предложу изменения — вы их просмотрите и примените.
          </p>
        )}
        {messages.map((m) => (
          <div key={m.id}>
            <div className={`text-[15px] leading-relaxed rounded-lg px-3 py-2.5 whitespace-pre-wrap ${m.role === 'user' ? 'bg-gray-50' : 'bg-pink-50'}`}>
              <span className="text-[10px] uppercase text-gray-400">{m.role === 'user' ? 'вы' : 'ИИ'}</span>
              <p className="text-gray-700">{m.content}</p>
            </div>
            {m.role === 'assistant' && !m.updates_applied && (m.updates?.length || 0) > 0 && (
              <ProposalBlock matter={matter} message={m} onMatterChanged={onMatterChanged} />
            )}
            {m.role === 'assistant' && m.updates_applied && (m.updates?.length || 0) > 0 && (
              <p className="mt-1 text-xs text-gray-400">Правки обработаны.</p>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Спросите по договору или попросите правку..."
          disabled={sending}
          className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] outline-none focus:ring-2 focus:ring-pink-400/50" />
        <button onClick={send} disabled={sending || !input.trim()}
          className="px-3 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 text-white disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
